import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ProfileCard from '../components/ProfileCard';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [profiles, setProfiles] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profilesRes, subRes, myRes, statsRes] = await Promise.all([
          api.get('/profiles/search?limit=6&page=1'),
          api.get('/subscriptions/my'),
          api.get('/profiles/me'),
          api.get('/dashboard/stats'),
        ]);
        setProfiles(profilesRes.data.profiles || []);
        setSubscription(subRes.data);
        setMyProfile(myRes.data.profile);
        setStats(statsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isTrial = subscription?.isOnTrial;
  const isActive = subscription?.subscription?.is_active;
  const planName = subscription?.subscription?.plan_name || 'Free Trial';
  const profileComplete = myProfile?.profile_status === 'active';

  return (
    <div className="min-h-screen bg-cream-100">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome banner */}
        <div className="bg-gold-gradient text-white rounded-2xl p-6 mb-8 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold">
              Good day, {user?.firstName}! 🙏
            </h2>
            <p className="text-cream-100 mt-1">
              {isTrial
                ? `You're on a free trial — expires ${new Date(subscription.trialEndsAt).toLocaleDateString('en-IN')}`
                : isActive
                ? `${planName} plan — expires ${new Date(subscription.subscription.expires_at).toLocaleDateString('en-IN')}`
                : 'Browse matches and find your life partner'
              }
            </p>
          </div>
          {!isActive && (
            <Link to="/subscription" className="btn-gold text-sm px-5 py-2.5 whitespace-nowrap">
              ✨ Upgrade Now
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Profile Views',     value: myProfile?.profile_views ?? '—', icon: '👁️', link: null },
            { label: 'Interests Received', value: stats?.interestsReceived ?? '—', icon: '💌', link: '/interests' },
            { label: 'Shortlisted',       value: stats?.shortlisted ?? '—', icon: '⭐', link: '/shortlist' },
            { label: 'Conversations',     value: stats?.conversations ?? '—', icon: '💬', link: '/chat' },
          ].map(({ label, value, icon, link }) => (
            <Link key={label} to={link || '#'} className="card p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
              <span className="text-2xl">{icon}</span>
              <div>
                <p className="font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Profile completion nudge — only show if profile is incomplete */}
        {!profileComplete && (
          <div className="card p-4 mb-8 flex items-center gap-4 bg-blue-50 border border-blue-100">
            <div className="text-2xl">📋</div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Complete your profile to get more matches!</p>
              <p className="text-sm text-gray-500">A complete profile gets 5x more responses.</p>
            </div>
            <Link to="/profile/setup" className="btn-primary text-sm py-2 px-4 whitespace-nowrap">
              Complete Profile
            </Link>
          </div>
        )}

        {/* Edit profile link — shown once profile is active */}
        {profileComplete && (
          <div className="card p-4 mb-8 flex items-center gap-4 bg-green-50 border border-green-100">
            <div className="text-2xl">✅</div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Your profile is active and visible to matches!</p>
              <p className="text-sm text-gray-500">Profile Score: {myProfile?.profile_score}%</p>
            </div>
            <Link to="/profile/setup" className="btn-outline text-sm py-2 px-4 whitespace-nowrap">
              Edit Profile
            </Link>
          </div>
        )}

        {/* Matches */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2"><img src="/cross-icon.png" alt="Cross" className="w-5 h-5 object-contain" /> Recommended Matches</h3>
          <Link to="/search" className="text-primary text-sm font-medium hover:underline">View all →</Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-56 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-8 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-5xl mb-4">💒</p>
            <p className="text-gray-500">No matches found yet. Try browsing all profiles!</p>
            <Link to="/search" className="btn-primary mt-4 inline-block">Browse Profiles</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map(p => <ProfileCard key={p.id} profile={p} />)}
          </div>
        )}

        {/* Verse */}
        <div className="mt-12 text-center text-gray-400 text-sm italic">
          <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 object-contain inline-block align-text-bottom mr-1" /> "He who finds a wife finds what is good and receives favor from the LORD." — Proverbs 18:22
        </div>
      </div>
    </div>
  );
}
